const LOCAL_SERVER_BASE_URL = 'http://localhost:5000'; // Make sure this is correct
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, signInWithCustomToken, onAuthStateChanged } from 'firebase/auth';
import {
  getFirestore,
  collection,
  addDoc,
  getDocs, // Keep getDocs for other uses if needed, but use getDoc for single doc check
  getDoc, // Import getDoc for efficient single document checks
  doc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  setDoc, // Import setDoc for creating a document with a specific ID
  serverTimestamp
} from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

// Custom Modal Component to replace alert/confirm
const CustomModal = ({ title, message, onClose }) => {
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full">
        <h3 className="text-xl font-bold text-gray-900 mb-4">{title}</h3>
        <p className="text-gray-700 mb-6">{message}</p>
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

// Utility for basic markdown-like formatting for lyrics
const formatLyrics = (text) => {
  if (!text) return null;
  // Bold: **text**
  text = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  // Italic: *text* or _text_
  text = text.replace(/(\*|_)(.*?)\1/g, '<em>$2</em>');
  // Underline: __text__ (custom for this app)
  text = text.replace(/__(.*?)__/g, '<u>$1</u>');
  // Newlines to <br>
  return text.split('\n').map((line, index) => (
    <React.Fragment key={index}>
      <span dangerouslySetInnerHTML={{ __html: line }} />
      <br />
    </React.Fragment>
  ));
};


const App = () => {
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [db, setDb] = useState(null);
  const [auth, setAuth] = useState(null);
  const [storage, setStorage] = useState(null);
  const [userId, setUserId] = useState(null);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [currentView, setCurrentView] = useState('list');
  const [editingSong, setEditingSong] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [songsPerPage] = useState(10); // Number of songs per page
  const [darkMode, setDarkMode] = useState(false); // Dark mode state

  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalMessage, setModalMessage] = useState('');

  const displayModal = (title, message) => {
    setModalTitle(title);
    setModalMessage(message);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalTitle('');
    setModalMessage('');
  };

  // Firebase Initialization and Authentication
  useEffect(() => {
    try {
  const firebaseConfig = {
    apiKey: "AIzaSyBJrdyBmxnvZOzAp-UhqLi4azC8u23QK9U",
    authDomain: "ogtdb-f3e7c.firebaseapp.com",
    projectId: "ogtdb-f3e7c",
    storageBucket: "ogtdb-f3e7c.firebasestorage.app",
    messagingSenderId: "376272816421",
    appId: "1:376272816421:web:8bf3dfc6ec1881874df2cc",
    measurementId: "G-B6H8V9TE8J"
  };
  const appId = firebaseConfig.appId; // Get appId directly from your config

      if (Object.keys(firebaseConfig).length === 0) {
        throw new Error("Firebase config is not available. Cannot initialize Firebase.");
      }

      const app = initializeApp(firebaseConfig);
      const firestoreDb = getFirestore(app);
      const firebaseAuth = getAuth(app);
      const firebaseStorage = getStorage(app);

      setDb(firestoreDb);
      setAuth(firebaseAuth);
      setStorage(firebaseStorage);

      const unsubscribe = onAuthStateChanged(firebaseAuth, async (user) => {
        if (user) {
          setUserId(user.uid);
          console.log("Authenticated user ID:", user.uid);
        } else {
          console.log("No user signed in. Attempting anonymous sign-in.");
          try {
            if (typeof __initial_auth_token !== 'undefined' && __initial_auth_token) {
              await signInWithCustomToken(firebaseAuth, __initial_auth_token);
              console.log("Signed in with custom token.");
            } else {
              await signInAnonymously(firebaseAuth);
              console.log("Signed in anonymously.");
            }
          } catch (authError) {
            console.error("Firebase Auth Error:", authError);
            setError(`Authentication failed: ${authError.message}. Please refresh.`);
          }
        }
        setIsAuthReady(true);
      });

      return () => unsubscribe();
    } catch (err) {
      console.error("Firebase initialization error:", err);
      setError(`Failed to initialize Firebase: ${err.message}`);
      setLoading(false);
    }
  }, []);

  // Fetch songs when auth is ready and db/userId are available
  useEffect(() => {
    if (!isAuthReady || !db || !userId) {
      console.log("Waiting for auth readiness, db, or userId...");
      return;
    }

    setLoading(true);
    setError('');
    console.log("Attempting to fetch songs for user:", userId);

    const songsCollectionRef = collection(db, `artifacts/${__app_id}/users/${userId}/songs`);
    const q = query(songsCollectionRef);

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const songsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSongs(songsData);
      setLoading(false);
      console.log("Songs fetched:", songsData.length);
    }, (err) => {
      console.error("Error fetching songs:", err);
      setError(`Failed to load songs: ${err.message}`);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [isAuthReady, db, userId]);

  // --- File Upload Helper Function ---
  const uploadFile = async (file, path) => {
    if (!storage || !userId || !file) return null;

    const fileRef = ref(storage, `artifacts/${__app_id}/users/${userId}/${path}/${file.name}_${Date.now()}`);
    try {
      const snapshot = await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      console.log(`File uploaded: ${file.name}, URL: ${downloadURL}`);
      return downloadURL;
    } catch (e) {
      console.error("Error uploading file:", e);
      throw new Error(`Failed to upload file: ${file.name}. ${e.message}`);
    }
  };

  // --- CRUD Operations ---

  const addSong = async (songData, albumCoverFile, musicFiles) => {
    if (!db || !userId) {
      displayModal('Error', 'Database not ready or user not authenticated.');
      return;
    }
    setLoading(true);
    setError('');
    let uploadedAlbumCoverUrl = '';
    const uploadedMusicFileUrls = [];

    try {
      if (albumCoverFile) {
        uploadedAlbumCoverUrl = await uploadFile(albumCoverFile, 'album_covers');
      }

      for (const file of musicFiles) {
        const url = await uploadFile(file, 'music_files');
        if (url) {
          uploadedMusicFileUrls.push(url);
        }
      }

      const songsCollectionRef = collection(db, `artifacts/${__app_id}/users/${userId}/songs`);
      await addDoc(songsCollectionRef, {
        ...songData,
        albumCoverUrl: uploadedAlbumCoverUrl,
        musicFileUrls: uploadedMusicFileUrls,
        lyricsHistory: [{ text: songData.lyrics, timestamp: serverTimestamp() }], // Initial history entry
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      displayModal('Success', 'Song added successfully!');
      setCurrentView('list');
    } catch (e) {
      console.error("Error adding document: ", e);
      setError(`Failed to add song: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const updateSong = async (id, updatedData, albumCoverFile, newMusicFiles) => {
    if (!db || !userId) {
      displayModal('Error', 'Database not ready or user not authenticated.');
      return;
    }
    setLoading(true);
    setError('');
    let newAlbumCoverUrl = updatedData.albumCoverUrl;
    let currentMusicFileUrls = updatedData.musicFileUrls || [];
    let currentLyricsHistory = updatedData.lyricsHistory || [];

    try {
      if (albumCoverFile) {
        newAlbumCoverUrl = await uploadFile(albumCoverFile, 'album_covers');
      }

      for (const file of newMusicFiles) {
        const url = await uploadFile(file, 'music_files');
        if (url) {
          currentMusicFileUrls.push(url);
        }
      }

      // Add current lyrics to history if they've changed
      if (editingSong && editingSong.lyrics !== updatedData.lyrics) {
        currentLyricsHistory = [...currentLyricsHistory, { text: editingSong.lyrics, timestamp: serverTimestamp() }];
      }

      const songDocRef = doc(db, `artifacts/${__app_id}/users/${userId}/songs`, id);
      await updateDoc(songDocRef, {
        ...updatedData,
        albumCoverUrl: newAlbumCoverUrl,
        musicFileUrls: currentMusicFileUrls,
        lyricsHistory: currentLyricsHistory, // Update history
        updatedAt: serverTimestamp()
      });
      displayModal('Success', 'Song updated successfully!');
      setCurrentView('list');
      setEditingSong(null);
    } catch (e) {
      console.error("Error updating document: ", e);
      setError(`Failed to update song: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const deleteSong = async (id, albumCoverUrl, musicFileUrls) => {
    if (!db || !userId || !storage) {
      displayModal('Error', 'Database or storage not ready or user not authenticated.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      if (albumCoverUrl) {
        try {
          const coverRef = ref(storage, albumCoverUrl);
          await deleteObject(coverRef);
          console.log("Album cover deleted from storage.");
        } catch (e) {
          console.warn("Could not delete album cover from storage (might not exist or permission issue):", e);
        }
      }

      if (musicFileUrls && Array.isArray(musicFileUrls)) {
        for (const url of musicFileUrls) {
          try {
            const musicRef = ref(storage, url);
            await deleteObject(musicRef);
            console.log("Music file deleted from storage:", url);
          } catch (e) {
            console.warn("Could not delete music file from storage (might not exist or permission issue):", url, e);
          }
        }
      }

      const songDocRef = doc(db, `artifacts/${__app_id}/users/${userId}/songs`, id);
      await deleteDoc(songDocRef);
      displayModal('Success', 'Song deleted successfully!');
    } catch (e) {
      console.error("Error deleting document: ", e);
      setError(`Failed to delete song: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  // --- Song Form Component ---
  const SongForm = ({ onSubmit, initialData = {}, onCancel }) => {
    const [title, setTitle] = useState(initialData.title || '');
    const [artist, setArtist] = useState(initialData.artist || '');
    const [album, setAlbum] = useState(initialData.album || '');
    const [lyrics, setLyrics] = useState(initialData.lyrics || '');
    const [dateCreated, setDateCreated] = useState(initialData.dateCreated || '');
    const [songwriters, setSongwriters] = useState(initialData.songwriters || '');
    const [composers, setComposers] = useState(initialData.composers || '');
    const [beatProducedBy, setBeatProducedBy] = useState(initialData.beatProducedBy || '');
    const [beatTempo, setBeatTempo] = useState(initialData.beatTempo || '');
    const [key, setKey] = useState(initialData.key || '');
    const [genre, setGenre] = useState(initialData.genre || '');
    const [specialNotes, setSpecialNotes] = useState(initialData.specialNotes || '');
    const [chords, setChords] = useState(initialData.chords || ''); // New state for chords

    const [albumCoverFile, setAlbumCoverFile] = useState(null);
    const [newMusicFiles, setNewMusicFiles] = useState([]);
    const [existingMusicFileUrls, setExistingMusicFileUrls] = useState(initialData.musicFileUrls || []);
    const [uploadingFiles, setUploadingFiles] = useState(false);

    const musicFileInputRef = useRef(null);

    const handleNewMusicFileChange = (e) => {
      const files = Array.from(e.target.files);
      setNewMusicFiles(prevFiles => [...prevFiles, ...files]);
      if (musicFileInputRef.current) {
        musicFileInputRef.current.value = '';
      }
    };

    const removeNewMusicFile = (indexToRemove) => {
      setNewMusicFiles(prevFiles => prevFiles.filter((_, index) => index !== indexToRemove));
    };

    const removeExistingMusicFileUrl = (urlToRemove) => {
      setExistingMusicFileUrls(prevUrls => prevUrls.filter(url => url !== urlToRemove));
    };

    // Drag and Drop handlers for music files
    const dragItem = useRef(null);
    const dragOverItem = useRef(null);

    const handleSort = () => {
      let _existingMusicFileUrls = [...existingMusicFileUrls];
      const draggedItemContent = _existingMusicFileUrls.splice(dragItem.current, 1)[0];
      _existingMusicFileUrls.splice(dragOverItem.current, 0, draggedItemContent);
      dragItem.current = null;
      dragOverItem.current = null;
      setExistingMusicFileUrls(_existingMusicFileUrls);
    };


    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!title.trim() || !lyrics.trim()) {
        displayModal('Input Required', 'Song title and lyrics are required.');
        return;
      }

      setUploadingFiles(true);

      const songData = {
        title,
        artist,
        album,
        lyrics,
        dateCreated,
        songwriters,
        composers,
        beatProducedBy,
        beatTempo: beatTempo ? Number(beatTempo) : '',
        key,
        genre,
        specialNotes,
        chords, // Include chords
        albumCoverUrl: initialData.albumCoverUrl || '',
        musicFileUrls: existingMusicFileUrls // Use the state that reflects removals and reordering
      };

      try {
        await onSubmit(songData, albumCoverFile, newMusicFiles);
      } catch (submitError) {
        console.error("Submission error:", submitError);
      } finally {
        setUploadingFiles(false);
      }
    };

    return (
      <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 mb-8">
        <h2 className="text-3xl font-bold text-indigo-700 mb-6 text-center">
          {initialData.id ? 'Edit Song' : 'Add New Song'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">Title <span className="text-red-500">*</span></label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              required
            />
          </div>
          <div>
            <label htmlFor="artist" className="block text-sm font-medium text-gray-700 mb-1">Artist</label>
            <input
              type="text"
              id="artist"
              value={artist}
              onChange={(e) => setArtist(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div>
            <label htmlFor="album" className="block text-sm font-medium text-gray-700 mb-1">Album</label>
            <input
              type="text"
              id="album"
              value={album}
              onChange={(e) => setAlbum(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div>
            <label htmlFor="dateCreated" className="block text-sm font-medium text-gray-700 mb-1">Date Created</label>
            <input
              type="date"
              id="dateCreated"
              value={dateCreated}
              onChange={(e) => setDateCreated(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div>
            <label htmlFor="songwriters" className="block text-sm font-medium text-gray-700 mb-1">Songwriter(s)</label>
            <input
              type="text"
              id="songwriters"
              value={songwriters}
              onChange={(e) => setSongwriters(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div>
            <label htmlFor="composers" className="block text-sm font-medium text-gray-700 mb-1">Composer(s)</label>
            <input
              type="text"
              id="composers"
              value={composers}
              onChange={(e) => setComposers(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div>
            <label htmlFor="beatProducedBy" className="block text-sm font-medium text-gray-700 mb-1">Beat Produced By</label>
            <input
              type="text"
              id="beatProducedBy"
              value={beatProducedBy}
              onChange={(e) => setBeatProducedBy(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div>
            <label htmlFor="beatTempo" className="block text-sm font-medium text-gray-700 mb-1">Beat Tempo (BPM)</label>
            <input
              type="number"
              id="beatTempo"
              value={beatTempo}
              onChange={(e) => setBeatTempo(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div>
            <label htmlFor="key" className="block text-sm font-medium text-gray-700 mb-1">Key</label>
            <input
              type="text"
              id="key"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div>
            <label htmlFor="genre" className="block text-sm font-medium text-gray-700 mb-1">Genre</label>
            <input
              type="text"
              id="genre"
              value={genre}
              onChange={(e) => setGenre(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* Album Cover Upload */}
          <div className="border border-dashed border-gray-300 p-4 rounded-md text-center bg-gray-50">
            <label htmlFor="albumCoverUpload" className="block text-sm font-medium text-gray-700 mb-2 cursor-pointer">
              Upload Album Cover (Image)
            </label>
            <input
              type="file"
              id="albumCoverUpload"
              accept="image/*"
              onChange={(e) => setAlbumCoverFile(e.target.files[0])}
              className="hidden"
            />
            {albumCoverFile ? (
              <p className="text-sm text-gray-600">{albumCoverFile.name}</p>
            ) : initialData.albumCoverUrl ? (
              <p className="text-sm text-gray-600">Current: <a href={initialData.albumCoverUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">View Image</a></p>
            ) : (
              <p className="text-sm text-gray-500">No file chosen</p>
            )}
            <label htmlFor="albumCoverUpload" className="mt-2 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer">
              Choose Album Cover
            </label>
          </div>

          {/* Multiple Music File Upload */}
          <div className="border border-dashed border-gray-300 p-4 rounded-md bg-gray-50">
            <label htmlFor="musicFileUpload" className="block text-sm font-medium text-gray-700 mb-2 cursor-pointer text-center">
              Upload Music Files (Audio, PDF, etc.)
            </label>
            <input
              type="file"
              id="musicFileUpload"
              multiple // Allow multiple file selection
              onChange={handleNewMusicFileChange}
              ref={musicFileInputRef} // Attach ref to clear input
              className="hidden"
            />
            <label htmlFor="musicFileUpload" className="mt-2 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer mx-auto block w-fit">
              Add More Files
            </label>

            {/* Display existing files */}
            {existingMusicFileUrls.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-700 mb-1">Existing Files (Drag to reorder):</p>
                <ul className="list-disc list-inside text-sm text-gray-600">
                  {existingMusicFileUrls.map((url, index) => (
                    <li
                      key={url}
                      className="flex items-center justify-between py-1 px-2 border border-gray-200 rounded-md mb-1 bg-white cursor-grab"
                      draggable
                      onDragStart={(e) => (dragItem.current = index)}
                      onDragEnter={(e) => (dragOverItem.current = index)}
                      onDragEnd={handleSort}
                      onDragOver={(e) => e.preventDefault()}
                    >
                      <span className="truncate mr-2">
                        <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
                          {url.substring(url.lastIndexOf('/') + 1).split('_')[0] || url}
                        </a>
                      </span>
                      <button
                        type="button"
                        onClick={() => removeExistingMusicFileUrl(url)}
                        className="text-red-500 hover:text-red-700 text-xs px-2 py-1 rounded-md border border-red-300 hover:border-red-500"
                        title="Remove this existing file"
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Display newly selected files */}
            {newMusicFiles.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-700 mb-1">New Files to Upload:</p>
                <ul className="list-disc list-inside text-sm text-gray-600">
                  {newMusicFiles.map((file, index) => (
                    <li key={index} className="flex items-center justify-between py-1 px-2 border border-gray-200 rounded-md mb-1 bg-white">
                      <span className="truncate mr-2">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => removeNewMusicFile(index)}
                        className="text-red-500 hover:text-red-700 text-xs px-2 py-1 rounded-md border border-red-300 hover:border-red-500"
                        title="Remove this new file"
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {(existingMusicFileUrls.length === 0 && newMusicFiles.length === 0) && (
                <p className="text-sm text-gray-500 text-center mt-2">No music files chosen</p>
            )}
          </div>

          <div>
            <label htmlFor="chords" className="block text-sm font-medium text-gray-700 mb-1">Chords</label>
            <textarea
              id="chords"
              value={chords}
              onChange={(e) => setChords(e.target.value)}
              rows="3"
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
              placeholder="e.g., (Am)Verse 1 starts here (G)with a chord..."
            ></textarea>
          </div>

          <div>
            <label htmlFor="lyrics" className="block text-sm font-medium text-gray-700 mb-1">Lyrics <span className="text-red-500">*</span></label>
            <textarea
              id="lyrics"
              value={lyrics}
              onChange={(e) => setLyrics(e.target.value)}
              rows="10"
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
              placeholder="Use **bold**, *italic*, __underline__ for formatting."
              required
            ></textarea>
          </div>
          <div>
            <label htmlFor="specialNotes" className="block text-sm font-medium text-gray-700 mb-1">Special Notes</label>
            <textarea
              id="specialNotes"
              value={specialNotes}
              onChange={(e) => setSpecialNotes(e.target.value)}
              rows="5"
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
            ></textarea>
          </div>
          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploadingFiles}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-200 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploadingFiles ? (
                <span className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                  Uploading...
                </span>
              ) : initialData.id ? 'Update Song' : 'Add Song'}
            </button>
          </div>
        </form>
      </div>
    );
  };

  // --- Song Item Component ---
  const SongItem = ({ song, onEdit, onDelete }) => {
    const [showFullLyrics, setShowFullLyrics] = useState(false);
    const [showMoreDetails, setShowMoreDetails] = useState(false);
    const [showLyricsHistory, setShowLyricsHistory] = useState(false);
    const [selectedHistoryVersion, setSelectedHistoryVersion] = useState(null);

    const detailItemClass = "text-gray-600 text-sm mb-1";
    const detailLabelClass = "font-medium text-gray-700 mr-2";

    const currentLyricsToDisplay = selectedHistoryVersion ? selectedHistoryVersion.text : song.lyrics;

    return (
      <div className="bg-white border border-gray-200 rounded-xl shadow-md flex flex-col overflow-hidden">
        {/* Album Cover Display */}
        <div className="relative w-full h-48 bg-gray-200 flex items-center justify-center overflow-hidden">
          {song.albumCoverUrl ? (
            <img
              src={song.albumCoverUrl}
              alt={`${song.title} Album Cover`}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'https://placehold.co/400x200/E0E7FF/3B82F6?text=No+Cover';
              }}
            />
          ) : (
            <div className="text-gray-500 text-sm">No Album Cover</div>
          )}
        </div>

        <div className="p-5 flex flex-col flex-grow">
          <h3 className="text-2xl font-semibold text-gray-900 mb-1">{song.title}</h3>
          <p className="text-gray-600 mb-2">
            {song.artist && <span className="font-medium">{song.artist}</span>}
            {song.artist && song.album && <span className="mx-1 text-gray-400">•</span>}
            {song.album && <span className="text-gray-500 italic">{song.album}</span>}
          </p>

          {/* Basic details always visible */}
          <div className="text-gray-700 text-sm mb-3">
            {song.genre && <span className="inline-block bg-indigo-100 text-indigo-800 text-xs font-semibold px-2.5 py-0.5 rounded-full mr-2">Genre: {song.genre}</span>}
            {song.beatTempo && <span className="inline-block bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded-full">BPM: {song.beatTempo}</span>}
          </div>

          {/* Collapsible More Details */}
          <button
            onClick={() => setShowMoreDetails(!showMoreDetails)}
            className="text-indigo-600 hover:underline text-sm mb-3 text-left"
          >
            {showMoreDetails ? 'Hide Details' : 'Show More Details'}
          </button>

          {showMoreDetails && (
            <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 mb-4 space-y-1">
              {song.dateCreated && <p className={detailItemClass}><span className={detailLabelClass}>Created:</span>{song.dateCreated}</p>}
              {song.songwriters && <p className={detailItemClass}><span className={detailLabelClass}>Songwriter(s):</span>{song.songwriters}</p>}
              {song.composers && <p className={detailItemClass}><span className={detailLabelClass}>Composer(s):</span>{song.composers}</p>}
              {song.beatProducedBy && <p className={detailItemClass}><span className={detailLabelClass}>Beat Produced By:</span>{song.beatProducedBy}</p>}
              {song.key && <p className={detailItemClass}><span className={detailLabelClass}>Key:</span>{song.key}</p>}

              {/* Display multiple music file links and audio players */}
              {song.musicFileUrls && song.musicFileUrls.length > 0 && (
                <div className={detailItemClass}>
                  <span className={detailLabelClass}>Music Files:</span>
                  <ul className="list-disc list-inside mt-1 space-y-2">
                    {song.musicFileUrls.map((url, index) => (
                      <li key={index} className="flex flex-col">
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline break-all mb-1"
                        >
                          {url.substring(url.lastIndexOf('/') + 1).split('_')[0] || `File ${index + 1}`}
                        </a>
                        {/* Check if it's an audio file before rendering audio tag */}
                        {(url.endsWith('.mp3') || url.endsWith('.wav') || url.endsWith('.ogg') || url.endsWith('.m4a')) && (
                          <audio controls className="w-full mt-1">
                            <source src={url} type={`audio/${url.split('.').pop()}`} />
                            Your browser does not support the audio element.
                          </audio>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {song.specialNotes && (
                <div className={detailItemClass}>
                  <span className={detailLabelClass}>Notes:</span>
                  <p className="whitespace-pre-wrap text-xs italic">{song.specialNotes}</p>
                </div>
              )}
            </div>
          )}

          {/* Chords Display */}
          {song.chords && (
            <div className="mb-4 p-3 bg-gray-100 rounded-md border border-gray-200">
              <h4 className="font-semibold text-gray-800 mb-1">Chords:</h4>
              <p className="font-mono text-sm text-gray-700 whitespace-pre-wrap">{song.chords}</p>
            </div>
          )}

          {/* Lyrics History */}
          {song.lyricsHistory && song.lyricsHistory.length > 1 && (
            <div className="mb-4">
              <button
                onClick={() => setShowLyricsHistory(!showLyricsHistory)}
                className="text-indigo-600 hover:underline text-sm"
              >
                {showLyricsHistory ? 'Hide Lyrics History' : 'View Lyrics History'}
              </button>
              {showLyricsHistory && (
                <div className="mt-2 p-3 bg-gray-100 rounded-md border border-gray-200">
                  <label htmlFor="lyricsVersion" className="block text-sm font-medium text-gray-700 mb-1">Select Version:</label>
                  <select
                    id="lyricsVersion"
                    onChange={(e) => {
                      const selectedIndex = parseInt(e.target.value);
                      setSelectedHistoryVersion(selectedIndex === -1 ? null : song.lyricsHistory[selectedIndex]);
                    }}
                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="-1">Current Version</option>
                    {song.lyricsHistory.map((version, index) => (
                      <option key={index} value={index}>
                        {`Version ${index + 1} (${new Date(version.timestamp?.toDate()).toLocaleString()})`}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}


          <div className="relative mb-4 flex-grow">
            <h4 className="font-semibold text-gray-800 mb-1">Lyrics:</h4>
            <p className={`text-gray-700 whitespace-pre-wrap ${!showFullLyrics ? 'line-clamp-4' : ''}`}>
              {formatLyrics(currentLyricsToDisplay)}
            </p>
            {currentLyricsToDisplay && currentLyricsToDisplay.split('\n').length > 4 && (
              <button
                onClick={() => setShowFullLyrics(!showFullLyrics)}
                className="text-indigo-600 hover:underline text-sm mt-2"
              >
                {showFullLyrics ? 'Show less lyrics' : 'Read full lyrics'}
              </button>
            )}
          </div>

          <div className="flex justify-end space-x-3 mt-4">
            <button
              onClick={() => onEdit(song)}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-200 shadow-sm"
            >
              Edit
            </button>
            <button
              onClick={() => onDelete(song.id, song.albumCoverUrl, song.musicFileUrls)}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-200 shadow-sm"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  // --- Main App Render Logic ---
  // Helper function to handle file downloads
  const downloadFile = (data, filename, type) => {
    const blob = new Blob([data], { type: type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Export functions
  const handleExportJson = () => {
    if (songs.length === 0) {
      displayModal('Export Error', 'No songs to export.');
      return;
    }
    const dataToExport = songs.map(({ createdAt, updatedAt, ...rest }) => {
      // Convert Firestore Timestamps in lyricsHistory to ISO strings for export
      const formattedHistory = (rest.lyricsHistory || []).map(version => ({
        text: version.text,
        timestamp: version.timestamp?.toDate ? version.timestamp.toDate().toISOString() : version.timestamp
      }));
      return { ...rest, lyricsHistory: formattedHistory };
    });
    const json = JSON.stringify(dataToExport, null, 2);
    downloadFile(json, 'my_music_repository.json', 'application/json');
  };

  const handleExportCsv = () => {
    if (songs.length === 0) {
      displayModal('Export Error', 'No songs to export.');
      return;
    }

    // Define headers based on all possible fields, ensuring order
    const headers = [
      'id', 'title', 'artist', 'album', 'dateCreated', 'songwriters', 'composers',
      'beatProducedBy', 'beatTempo', 'key', 'genre', 'chords', 'lyrics', 'specialNotes',
      'albumCoverUrl', 'musicFileUrls', 'lyricsHistory' // Include new fields
    ];

    // Create CSV header row
    let csv = headers.map(header => `"${header}"`).join(',') + '\n';

    // Create CSV data rows
    songs.forEach(song => {
      const row = headers.map(header => {
        let value = song[header];
        if (header === 'musicFileUrls' && Array.isArray(value)) {
          value = value.join(';'); // Join multiple URLs with a semicolon
        } else if (header === 'lyricsHistory' && Array.isArray(value)) {
          // Serialize lyrics history to a JSON string
          value = JSON.stringify(value.map(v => ({
            text: v.text,
            timestamp: v.timestamp?.toDate ? v.timestamp.toDate().toISOString() : v.timestamp
          })));
        }
        if (value === null || value === undefined) {
          value = '';
        }
        // Escape double quotes by doubling them, and wrap in quotes to handle commas/newlines
        return `"${String(value).replace(/"/g, '""')}"`;
      }).join(',');
      csv += row + '\n';
    });

    downloadFile(csv, 'my_music_repository.csv', 'text/csv');
  };

  // Import functionality
  const handleImportJson = (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      setLoading(true); // Move loading start here
      setError(''); // Clear previous errors
      try {
        const importedSongs = JSON.parse(e.target.result);
        if (!Array.isArray(importedSongs)) {
          displayModal('Import Error', 'Invalid JSON format. Expected an array of songs.');
          // Do not set loading to false here, it's handled in finally
          // Do not clear file input here, it's handled in finally
          return; // Exit early
        }

        let importedCount = 0;
        let updatedCount = 0;

        for (const songData of importedSongs) {
          // Remove ID and timestamps for new additions, or use ID for updates
          const { id, createdAt, updatedAt, lyricsHistory, ...rest } = songData;

          // Convert ISO strings back to Date objects for lyricsHistory if present
          const formattedLyricsHistory = (lyricsHistory || []).map(version => ({
            text: version.text,
            timestamp: version.timestamp ? new Date(version.timestamp) : null
          }));

          const songToSave = {
            ...rest,
            lyricsHistory: formattedLyricsHistory,
            // Ensure musicFileUrls is an array, even if empty
            musicFileUrls: Array.isArray(rest.musicFileUrls) ? rest.musicFileUrls : (rest.musicFileUrls ? [rest.musicFileUrls] : [])
          };

          if (id) {
            // Check if song with this ID already exists to decide between setDoc and addDoc
            const docRef = doc(db, `artifacts/${__app_id}/users/${userId}/songs`, id);
            const docSnap = await getDoc(docRef); // Use getDoc for a single document
            const exists = docSnap.exists();

            if (exists) {
                await updateDoc(docRef, { ...songToSave, updatedAt: serverTimestamp() });
                updatedCount++;
            } else {
                // If ID exists in JSON but not in Firestore, add it as a new doc with specified ID
                await setDoc(docRef, { ...songToSave, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
                importedCount++;
            }
          } else {
            // Add as a new document if no ID is provided in the JSON
            await addDoc(collection(db, `artifacts/${__app_id}/users/${userId}/songs`), {
              ...songToSave,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            });
            importedCount++;
          }
        }
        displayModal('Import Complete', `Successfully imported ${importedCount} new songs and updated ${updatedCount} existing songs.`);
      } catch (err) { // Catch all errors here
        console.error("Error during import process:", err);
        displayModal('Import Error', `Failed to import songs: ${err.message}`);
      } finally {
        setLoading(false);
        event.target.value = ''; // Clear file input
      }
    };
    reader.readAsText(file);
  };

  // Client-side sorting logic
  const getSortedSongs = () => {
    const sortableSongs = [...songs];

    sortableSongs.sort((a, b) => {
      let valA = a[sortBy];
      let valB = b[sortBy];

      if (sortBy === 'createdAt') {
        // Handle Firestore Timestamps for createdAt
        valA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
        valB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
      } else {
        // Handle null/undefined values for other fields
        if (valA === undefined || valA === null) valA = '';
        if (valB === undefined || valB === null) valB = '';

        // Convert to lowercase for string comparison to ensure case-insensitivity
        if (typeof valA === 'string' && typeof valB === 'string') {
          valA = valA.toLowerCase();
          valB = valB.toLowerCase();
        }
      }

      if (valA < valB) {
        return sortOrder === 'asc' ? -1 : 1;
      }
      if (valA > valB) {
        return sortOrder === 'asc' ? 1 : -1;
      }
      return 0;
    });
    return sortableSongs;
  };

  const filteredAndSortedSongs = getSortedSongs().filter(song => {
    const lowerCaseSearchTerm = searchTerm.toLowerCase();
    const searchableFields = [
      song.title, song.artist, song.album, song.lyrics, song.genre,
      song.songwriters, song.composers, song.specialNotes, song.chords // Include chords in search
    ];
    return searchableFields.some(field =>
      field && String(field).toLowerCase().includes(lowerCaseSearchTerm)
    );
  });

  // Pagination Logic
  const indexOfLastSong = currentPage * songsPerPage;
  const indexOfFirstSong = indexOfLastSong - songsPerPage;
  const currentSongs = filteredAndSortedSongs.slice(indexOfFirstSong, indexOfLastSong);
  const totalPages = Math.ceil(filteredAndSortedSongs.length / songsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Dark Mode Toggle
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);


  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-gray-100' : 'bg-gradient-to-br from-blue-50 to-indigo-100 text-gray-800'} p-4 font-sans transition-colors duration-300`}>
      <div className={`max-w-4xl mx-auto ${darkMode ? 'bg-gray-800 shadow-xl' : 'bg-white shadow-2xl'} rounded-xl p-6 md:p-8`}>
        <div className="flex justify-between items-center mb-4">
          <h1 className={`text-4xl font-extrabold text-center ${darkMode ? 'text-indigo-400' : 'text-indigo-700'} tracking-tight`}>
            My Music Lyrics Repository
          </h1>
          <button
            onClick={() => setDarkMode(!darkMode)}
            className={`p-2 rounded-full ${darkMode ? 'bg-gray-700 text-yellow-300' : 'bg-gray-200 text-gray-700'} transition-colors duration-300`}
            title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {darkMode ? '☀️' : '🌙'}
          </button>
        </div>

        {userId && (
          <p className={`text-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-6`}>
            Your User ID: <span className={`font-mono ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-xs'} p-1 rounded-md`}>{userId}</span>
          </p>
        )}

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg relative mb-6" role="alert">
            <strong className="font-bold">Error!</strong>
            <span className="block sm:inline ml-2">{error}</span>
          </div>
        )}

        {loading && (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-500"></div>
            <p className="ml-4 text-xl text-indigo-600">Loading data...</p>
          </div>
        )}

        {!loading && !error && (
          <>
            {currentView === 'list' && (
              <>
                <div className="flex flex-col sm:flex-row justify-between items-center mb-6 space-y-4 sm:space-y-0 sm:space-x-4">
                  <input
                    type="text"
                    placeholder="Search songs, artists, lyrics, chords..."
                    className={`flex-grow p-3 border ${darkMode ? 'border-gray-600 bg-gray-700 text-gray-100' : 'border-gray-300 bg-white text-gray-800'} rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-base`}
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1); // Reset to first page on search
                    }}
                  />
                  <button
                    onClick={() => setCurrentView('add')}
                    className="bg-indigo-600 text-white py-2 px-5 rounded-lg hover:bg-indigo-700 transition-colors duration-300 shadow-md flex items-center flex-shrink-0"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path></svg>
                    Add New Song
                  </button>
                </div>

                {/* Sorting and Export/Import Options */}
                <div className="flex flex-col sm:flex-row justify-between items-center mb-6 space-y-3 sm:space-y-0 sm:space-x-4">
                  <div className="flex items-center space-x-2 w-full sm:w-auto">
                    <label htmlFor="sortBy" className={`font-medium text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Sort by:</label>
                    <select
                      id="sortBy"
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className={`p-2 border ${darkMode ? 'border-gray-600 bg-gray-700 text-gray-100' : 'border-gray-300 bg-white text-gray-800'} rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm`}
                    >
                      <option value="createdAt">Date Created</option>
                      <option value="title">Title</option>
                      <option value="artist">Artist</option>
                      <option value="album">Album</option>
                      <option value="genre">Genre</option>
                      <option value="beatTempo">BPM</option>
                    </select>
                    <button
                      onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                      className={`p-2 border ${darkMode ? 'border-gray-600 bg-gray-700 text-gray-100' : 'border-gray-300 bg-gray-100 text-gray-700'} rounded-md shadow-sm hover:bg-gray-200 transition-colors duration-200 ${darkMode ? 'hover:bg-gray-600' : ''}`}
                      title={sortOrder === 'asc' ? 'Sort Descending' : 'Sort Ascending'}
                    >
                      {sortOrder === 'asc' ? '↑' : '↓'}
                    </button>
                  </div>

                  <div className="flex space-x-3 w-full sm:w-auto justify-end">
                    <button
                      onClick={handleExportJson}
                      className="bg-green-500 text-white py-2 px-4 rounded-lg hover:bg-green-600 transition-colors duration-300 shadow-sm text-sm"
                    >
                      Export JSON
                    </button>
                    <button
                      onClick={handleExportCsv}
                      className="bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors duration-300 shadow-sm text-sm"
                    >
                      Export CSV
                    </button>
                    <label className="bg-purple-500 text-white py-2 px-4 rounded-lg hover:bg-purple-600 transition-colors duration-300 shadow-sm text-sm cursor-pointer">
                      Import JSON
                      <input type="file" accept=".json" className="hidden" onChange={handleImportJson} />
                    </label>
                  </div>
                </div>


                {/* Filtered and Sorted songs display */}
                {filteredAndSortedSongs.length === 0 ? (
                  <div className={`text-center py-10 ${darkMode ? 'text-gray-400' : 'text-gray-500'} text-lg`}>
                    No songs found matching your search or filters.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {currentSongs.map(song => (
                      <SongItem
                        key={song.id}
                        song={song}
                        onEdit={(songToEdit) => {
                          setEditingSong(songToEdit);
                          setCurrentView('edit');
                        }}
                        onDelete={deleteSong}
                      />
                    ))}
                  </div>
                )}

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center mt-8 space-x-2">
                    <button
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`px-4 py-2 rounded-lg ${darkMode ? 'bg-gray-700 text-gray-100 disabled:bg-gray-800 disabled:text-gray-500' : 'bg-indigo-500 text-white disabled:bg-indigo-300'} hover:bg-indigo-600 disabled:opacity-50 transition-colors duration-200`}
                    >
                      Previous
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                      <button
                        key={number}
                        onClick={() => paginate(number)}
                        className={`px-4 py-2 rounded-lg ${currentPage === number ? (darkMode ? 'bg-indigo-600 text-white' : 'bg-indigo-700 text-white') : (darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700')} hover:bg-indigo-600 transition-colors duration-200`}
                    >
                      {number}
                    </button>
                    ))}
                    <button
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`px-4 py-2 rounded-lg ${darkMode ? 'bg-gray-700 text-gray-100 disabled:bg-gray-800 disabled:text-gray-500' : 'bg-indigo-500 text-white disabled:bg-indigo-300'} hover:bg-indigo-600 disabled:opacity-50 transition-colors duration-200`}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}

            {(currentView === 'add' || currentView === 'edit') && (
              <SongForm
                onSubmit={currentView === 'add' ? addSong : (data, albumCoverFile, newMusicFiles) => updateSong(editingSong.id, data, albumCoverFile, newMusicFiles)}
                initialData={editingSong || {}}
                onCancel={() => {
                  setCurrentView('list');
                  setEditingSong(null);
                }}
              />
            )}
          </>
        )}
      </div>

      {showModal && (
        <CustomModal
          title={modalTitle}
          message={modalMessage}
          onClose={closeModal}
        />
      )}
    </div>
  );
};

export default App;
